import { Injectable } from '@angular/core';
import { firstValueFrom, map, ReplaySubject, take } from 'rxjs';
import { DialogService } from '../utils/dialog/dialog.service';
import { FeatureDetectionService } from './feature-detection.service';
import { ScriptLoadService } from './script-load.service';
import { SettingsService } from './settings.service';

@Injectable({
    providedIn: 'root',
})
export class SeezService {
    private readonly seezLoaded$ = new ReplaySubject<boolean>(1);
    private readonly seezListingIdMap = new Map<string, string>();
    private activeSeezApiUrl?: string;

    constructor(
        private readonly dialogService: DialogService,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly settingsService: SettingsService,
        private readonly scriptLoadService: ScriptLoadService
    ) {}

    /**
     * Call in the onInit in your component.
     * NOTE: Due to limitations in the script it is only possible to have Seez for one id loaded at a time.
     * Thus, a reload will happen if client id changes, and you should not call this with differing ids on the same page.
     *
     * @param businessId The businessId for the Seez config that should be loaded.
     */
    public async initSeezSdk(businessId?: string): Promise<boolean> {
        if (this.featureDetectionService.isAudit() || !this.featureDetectionService.isBrowser()) {
            return false;
        }

        const seezSetup =
            (await firstValueFrom(
                this.settingsService.get().pipe(
                    take(1),
                    map((settings) => settings.seezSetup)
                )
            )) || {};

        if (!seezSetup.enabled || !seezSetup.apiUrl?.url) {
            return false;
        }

        const seezClientId = businessId ? seezSetup.idMap[businessId] : Object.values(seezSetup.idMap)[0];
        if (!seezClientId) {
            return false;
        }
        const apiUrl: string = `${seezSetup.apiUrl.url}?platform=${seezClientId}`;

        // Seez previously loaded.
        if (this.activeSeezApiUrl) {
            // With correct client id for site (return isLoaded).
            if (this.activeSeezApiUrl === apiUrl) {
                return firstValueFrom(this.getIsSeezLoaded());
            }
            // Incorrect client id for site (need to reload since SDK only allows one id active at a time and there's no way to change it on the fly).
            if (this.activeSeezApiUrl !== apiUrl) {
                window.location.reload(); // Yuck, but no choice.
                return false;
            }
        }

        this.activeSeezApiUrl = apiUrl;
        this.scriptLoadService.loadScript(apiUrl, {
            async: false,
            type: 'module',
            dataset: { 'seez-client-id': seezClientId },
            callback: () => {
                this.seezLoaded$.next(true);
            },
            errorCallback: () => {
                console.error('Error when loading Seez SDK.');
            },
        });

        return firstValueFrom(this.seezLoaded$);
    }

    /**
     * Subscribe to Seez SDK loaded state.
     */
    public getIsSeezLoaded() {
        return this.seezLoaded$.asObservable();
    }

    /**
     * Call this from components to initialize Seez buttons once SDK is loaded.
     */
    public initSeezButtons() {
        if (this.featureDetectionService.isBrowser() && typeof window.seezSdk?.injectSeezOnlineBuying === 'function') {
            window.seezSdk.injectSeezOnlineBuying(true).catch(console.error);
        }
    }

    /**
     * Gets the corresponding Seez listing-id for the elastic product id used by the PDP.
     *
     * @param productId - The id used by elastic / our website.
     */
    public async getSeezListingId(productId: string) {
        if (!this.featureDetectionService.isBrowser()) {
            throw new Error(`Do not call "getSeezListingId" outside of browser context! Id used: "${productId}"`);
        }

        let seezListingId = this.seezListingIdMap.get(productId) ?? '';
        if (!seezListingId) {
            seezListingId =
                (await window.seezSdk
                    ?.queryApi(`query b{listingsByExternalId(externalId: ["${productId}"])}`)
                    .then((value) => String(value?.listingsByExternalId?.[0] ?? ''))
                    .catch(() => '')) ?? '';
        }
        this.seezListingIdMap.set(productId, seezListingId);

        if (!seezListingId) {
            console.warn('SeezListingId not found for: ', productId);
        }

        return seezListingId;
    }
}

declare global {
    interface Window {
        seezSdk?: {
            injectSeezOnlineBuying: (forceRefresh?: boolean, callback?: (links: HTMLAnchorElement[]) => void) => Promise<void>;
            clientId: string;
            queryApi: (query: string) => Promise<{ listingsByExternalId: number[] }>;
            showModal: (modalType: string, options: { id: string; ['lead-type']: string }) => void;
        };
    }
}
